import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { COURSE_SETS, BRIEF_STYLE_TO_VENUE_STYLE } from "@bridepath/shared";
import { apiGet, apiPost } from "../lib/api.js";
import { AuthedImage } from "../lib/AuthedImage.js";

const API_BASE = (): string =>
  (window as unknown as { __BRIDEPATH_API__?: string }).__BRIDEPATH_API__ ?? "";

// ─── Types ───────────────────────────────────────────────────────────────────
interface Brief {
  area?: string; date_local?: string; season?: string;
  guests?: number; budget_cents?: number; currency?: string;
  styles?: string[]; categories?: string[]; palette?: string[]; notes?: string;
  photography_style?: string;
  food_service?: "buffet" | "sit_down"; food_courses?: number;
  menu_dishes?: { course: string; dish: string }[];
}
interface TurnResponse {
  wedding_id: string; wedding_day_id: string; brief: Brief;
  completeness: number; next_question: string | null; ready_to_generate: boolean;
}
// A reference tile = one wedding-relevant image, captioned by WHAT IT SHOWS
// (a venue space, a flower type, a scene) — never the vendor name. `vendor_id`
// is carried only so the builder can lock the chosen vendor for generation.
// `over_budget` = the vendor was pulled back in over the budget cap (min-pool
// top-up) — badged "a stretch", never silently mixed in.
interface Tile {
  image_id: string; label: string; vendor_id: string; vendor_name: string; over_budget?: boolean;
  // v17 annotation payload (dream-wedding-annotation §5.3) — optional so the UI
  // tolerates pre-backfill rows / an older API.
  flower_species?: string[]; flower_colors?: string[];
  venue_style?: string | null; ref_suitable?: boolean | null;
  // Per-VENDOR cheapest-credible category cost (whole-wedding cents). Absent when
  // the vendor has no price signal (refuse-to-guess) — the tile shows no price.
  cost_low_cents?: number; cost_high_cents?: number;
  cost_kind?: "published" | "estimated" | "band";
}
// What we capture per picked tile (keyed by "vendorId:imageId") so the rail
// labels, the no-repeat species filter, AND the running cost tally all survive
// bucket/sub-filter changes. Cost is the picked tile's vendor's category cost.
interface PickMeta {
  label: string; vendorId: string; species?: string[];
  costLowCents?: number | undefined; costHighCents?: number | undefined;
}
// The Music step's vendor choice (DJ vs live band) + its cost (so it feeds the
// running tally, mirroring the breakdown which costs the music vendor).
interface MusicPick {
  cat: "dj" | "band"; vendorId: string; name: string;
  costLowCents?: number | undefined; costHighCents?: number | undefined;
}
// Inventory-derived filter chips returned by /references (florals/venue only),
// plus colors_applied = which requested buckets survived the server's auto-relax.
interface ColorChip { bucket: string; label: string; swatch_hex: string; count: number }
interface VenueStyleChip { style: string; label: string; count: number }
interface RefChipMeta { color_chips?: ColorChip[]; venue_style_chips?: VenueStyleChip[]; colors_applied?: string[] }
// One generate() selection (SelectionV2, annotation plan §5.1): legacy vendor-id
// string, or the picked vendor + chosen reference image(s) — image_ids florals
// only, cap 3.
type SelectionEntry = string | { vendor_id: string; image_id?: string; image_ids?: string[] };
// Per-section budget guide from /references (computed server-side from the
// couple's total budget × the category's researched share × a 20% buffer).
interface BudgetMeta {
  allocated_cents: number; cap_cents: number; pct: number;
  excluded_over_budget: number; relaxed: boolean;
}
interface MomentOut { id: string; label: string; ordinal: number; status: string; image_url: string | null }
interface PlannedLabel { label: string; ordinal: number }
interface JobResponse {
  job: {
    id: string; status: "queued" | "running" | "done" | "failed" | "partial"; error: string | null;
    // The full ordered scene plan (0032) — the checklist shows ALL of these from
    // the first poll, so it never grows 6→10 mid-flight. Empty on legacy jobs.
    planned_labels?: PlannedLabel[];
  };
  moments: MomentOut[];
}

// ─── The build — Foundation (step 0) first, then venue (the look everything
// anchors to) ────────────────────────────────────────────────────────────────
// Step 0 is its OWN "The Basics" foundation step (area/guests/budget/season/
// style) — no image grid; its submit creates the wedding + seeds the brief, then
// advances to the venue. The remaining sections each render a reference grid. A
// SECTION is no longer 1:1 with a
// vendor category: ceremony/reception ride the locked venue's pool, Her/His Look
// pool bridal_attire / groom_attire (0031 split — different vendors), hair/
// nails/makeup split hair_makeup. `lock` is the
// taxonomy category a section's picks contribute to for the generation lock.
interface Step {
  section: string; cat: string; lock: string | null;
  title: string; lead: string; q: string; gating: boolean;
  /** ceremony/reception: scope images to the locked venue vendor when chosen. */
  scopeVenue?: boolean;
  /** non-grid steps: the Look's style picker / the Music vendor picker. */
  picker?: "photo" | "music";
  /** max picks per section — default 1 (radio: tapping another swaps); florals 3. */
  maxPicks?: number;
}
const STEPS: ReadonlyArray<Step> = [
  { section: "foundation", cat: "", lock: null, title: "The Basics", lead: "1 · The foundation", q: "Start here — a few essentials about your day so every picture we generate fits it. Set where, how many guests, your budget, the season, and the styles you love, then continue to your venue.", gating: false },
  { section: "venue", cat: "venue", lock: "venue", maxPicks: 4, title: "The Venue", lead: "2 · The venue", q: "Where it all happens. Pick up to 4 spaces at your venue — we'll render your wedding in each of them.", gating: true },
  { section: "florals", cat: "florals", lock: "florals", maxPicks: 3, title: "The Florals", lead: "3 · The flowers", q: "Choose the bouquets and installations you're drawn to. These flowers recur through your whole day.", gating: false },
  { section: "ceremony", cat: "venue", lock: "venue", scopeVenue: true, maxPicks: 4, title: "The Ceremony", lead: "4 · The vows", q: "The aisle, the altar, the moment itself — pick up to 4 ceremony settings at your venue.", gating: false },
  { section: "catering", cat: "catering", lock: "catering", title: "The Table Setting", lead: "5 · The tablescape", q: "The table itself — linens, place settings, centerpieces, glassware. Pick the table designs you want every table to match.", gating: false },
  { section: "food", cat: "catering", lock: "catering", title: "The Food", lead: "6 · The menu", q: "What's served — buffet or a plated sit-down dinner? Pick how many courses, then the dishes that make your menu.", gating: false },
  { section: "cake", cat: "cake", lock: "cake", title: "The Cake", lead: "7 · Something sweet", q: "Pick a cake style for the celebration.", gating: false },
  { section: "reception", cat: "venue", lock: "venue", scopeVenue: true, maxPicks: 4, title: "The Reception", lead: "8 · The party", q: "The room in full swing — pick up to 4 reception looks at your venue.", gating: false },
  { section: "attire_her", cat: "bridal_attire", lock: "bridal_attire", title: "Her Look", lead: "9 · The dress", q: "Gowns and silhouettes — pick the bridal looks that feel like you.", gating: false },
  { section: "attire_him", cat: "groom_attire", lock: "groom_attire", title: "His Look", lead: "10 · The suit", q: "Suits and tuxedos — pick the styles that fit the day.", gating: false },
  { section: "hair", cat: "hair_makeup", lock: "hair_makeup", title: "The Hair", lead: "11 · Beauty", q: "Updos, braids, waves — pick the wedding hair you're picturing.", gating: false },
  { section: "nails", cat: "hair_makeup", lock: "hair_makeup", title: "The Nails", lead: "12 · Beauty", q: "A detail that shows up in every ring shot — pick the manicure styles you love.", gating: false },
  { section: "music", cat: "dj", lock: null, picker: "music", title: "The Music", lead: "13 · The soundtrack", q: "DJ or live band? Pick who sets the energy for your night.", gating: false },
  { section: "photography", cat: "photography", lock: null, picker: "photo", title: "The Photography Style", lead: "14 · How it's shot", q: "Choose the photographic style your generated wedding pictures are rendered in — it's applied to every image we create.", gating: false },
];

// Per-step sub-filters, keyed by SECTION. Venue → space (real scene_tags;
// Ceremony/Reception are now their own sections, so only Indoor/Outdoor remain).
// The Food step has its own dedicated menu bar (service / courses) below — the
// old Table dining tabs moved there.
const SUB_FILTERS: Record<string, { param: "space" | "dining"; opts: { v: string; l: string }[] }> = {
  venue: {
    param: "space",
    opts: [
      { v: "indoor", l: "Indoor" }, { v: "outdoor", l: "Outdoor" },
    ],
  },
};

// The Food step's course tabs — labels for the shared COURSE_SETS vocabulary.
const COURSE_LABELS: Record<string, string> = {
  hors_doeuvre: "Hors d'oeuvres", appetizer: "Appetizer", entree: "Entrée",
  side: "Salad / intermezzo", dessert: "Dessert",
};

// Honest empty-state copy for the hard-floor sections (the server returns an
// EMPTY grid rather than wrong-subject images there — by design).
const EMPTY_HINTS: Record<string, string> = {
  ceremony: "No ceremony settings at your venue or similar ones yet — describe the ceremony you're picturing below and I'll match the vibe.",
  reception: "No reception settings at your venue or similar ones yet — describe the reception you're picturing below and I'll match the vibe.",
  nails: "Nail inspiration is thinner locally — describe the manicure you're picturing below and I'll match the vibe.",
  hair: "Hair inspiration is thinner locally — describe the style you're picturing below and I'll match the vibe.",
  attire_her: "Local bridal references are limited — describe the gown or dress you're picturing below.",
  attire_him: "Local menswear references are limited — describe the suit or tux you're picturing below.",
  food: "No dishes match that course locally yet — try another course tab, or describe your menu below.",
};

// The "Look" step's photographic treatments — must mirror the shared
// PhotographyStyle enum + the deriveStyleSheet PHOTO_TREATMENT map.
const PHOTO_STYLES: { v: string; l: string; d: string }[] = [
  { v: "editorial", l: "Editorial", d: "Magazine-grade, refined, deliberate" },
  { v: "fine_art", l: "Fine art", d: "Painterly, soft pastel, film grain" },
  { v: "light_airy", l: "Light & airy", d: "Bright, luminous, fresh whites" },
  { v: "moody", l: "Dark & moody", d: "Low light, rich shadows, cinematic" },
  { v: "documentary", l: "Documentary", d: "Candid, natural light, unposed" },
  { v: "romantic", l: "Romantic", d: "Warm golden glow, dreamy, soft" },
];

const SEASONS = ["spring", "summer", "fall", "winter"] as const;
// Must match the BriefStyle enum in @bridepath/shared (max 4 selectable) — an
// invalid value would fail the brief's reload validation and wipe it.
const STYLE_OPTS = ["classic", "modern", "rustic", "boho", "garden", "industrial", "luxury", "minimal", "vintage", "tropical"];
const MAX_STYLES = 4;

const dollars = (cents?: number): string =>
  cents == null ? "—" : `$${Math.round(cents / 100).toLocaleString()}`;

// Full range for the tally bar ("$24,000–$38,000"); compact for the small tiles
// ("$24k–$38k", "$1.2M") so a price fits on a 1/3-width card.
const range = (lo: number, hi: number): string => (lo === hi ? dollars(lo) : `${dollars(lo)}–${dollars(hi)}`);
const compactDollars = (cents: number): string => {
  const d = Math.round(cents / 100);
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(d % 1_000_000 === 0 ? 0 : 1)}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}k`;
  return `$${d.toLocaleString()}`;
};
const costRangeShort = (lo?: number, hi?: number): string | null =>
  lo == null || hi == null ? null : lo === hi ? compactDollars(lo) : `${compactDollars(lo)}–${compactDollars(hi)}`;

// "garden_rose" → "Garden Rose" for the species-filter chip copy.
const titleize = (s: string): string =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Per-moment progress verb for the "Generating the venue…" line.
const VERB: Record<string, string> = {
  venue: "Generating the venue", florals: "Painting the florals", tablescape: "Setting the table",
  ceremony: "Composing the ceremony", reception: "Lighting the reception",
  "first dance": "Cueing the first dance", cake: "Finishing the cake",
  attire: "Fitting the attire", "bridal portrait": "Styling the bridal portrait",
  "dance floor": "Lighting the dance floor", "live band": "Cueing the band",
};
const verbFor = (label: string): string => VERB[label.toLowerCase()] ?? `Composing the ${label.toLowerCase()}`;

// The provisional checklist before the job has created its moment rows.
const PROVISIONAL = ["Venue", "Florals", "Tablescape", "Ceremony", "Reception", "First Dance", "Cake"];

// Six fixed, well-spread scatter POSITIONS (non-overlapping). Caps simultaneously
// -animating cards at 6 (perf budget). Each card swaps through RANDOM pictures
// from the 50-image RAG pool and runs on its OWN randomized timer (delay), so no
// two cards fade in/out together. The SIZE tier, drift variant, and aspect ratio
// are assigned per-card at random — differently-sized cards each run.
const POSITIONS: ReadonlyArray<{ x: string; y: string }> = [
  { x: "20%", y: "26%" },
  { x: "76%", y: "22%" },
  { x: "82%", y: "68%" },
  { x: "16%", y: "72%" },
  { x: "50%", y: "15%" },
  { x: "48%", y: "84%" },
];
// Card widths — varied sizes for visual interest. Timing is fixed at 14s
// (3s in / 8s hold / 3s out) for every card; independence comes from each card's
// randomized --delay, not a per-size duration.
const SIZE_WIDTHS = ["min(22vw,180px)", "min(30vw,240px)", "min(38vw,300px)"] as const;
// Fixed aspect ratios assigned once per card (so swapping images object-covers
// into a stable box — no layout shift / CLS when the picture changes).
const ASPECTS = ["3 / 4", "4 / 3", "1 / 1", "4 / 5"] as const;
const FLOAT_VARIANTS = ["bp-float", "bp-float-a", "bp-float-b"] as const;
// A tiny deterministic PRNG so a card's size/variant/aspect are stable for the
// whole overlay lifetime (no reshuffle on re-render).
const seededRng = (seed: number) => () => {
  seed = (seed * 1664525 + 1013904223) >>> 0;
  return seed / 0xffffffff;
};

export function Plan() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><PlanInner /></SignedIn>
    </>
  );
}

function PlanInner() {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  // Ref-stable getToken so <AuthedImage> fetch effects don't re-run on re-render.
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const stableGetToken = useCallback(() => tokenRef.current(), []);

  const [phase, setPhase] = useState<"builder" | "review" | "composing">("builder");
  const [weddingId, setWeddingId] = useState<string | null>(null);
  const [brief, setBrief] = useState<Brief>({});
  const [stepIdx, setStepIdx] = useState(0);

  // Foundations (captured inside the venue step so matching has enough to rank).
  const [essentialsDone, setEssentialsDone] = useState(false);
  const [area, setArea] = useState("New York City");
  const [guests, setGuests] = useState<number | null>(null);
  const [budget, setBudget] = useState<number | null>(null);
  const [season, setSeason] = useState<string | null>(null);
  const [styles, setStyles] = useState<string[]>([]);

  // Reference tiles keyed by a COMPOSITE key (cat + sub-filter + scoped vendor)
  // so the venue space tabs / dining tabs each cache + paginate independently.
  const [tilesByCat, setTilesByCat] = useState<Record<string, Tile[]>>({});
  // The venue step's "out of budget" group (over-cap venues) per composite key.
  const [overTilesByKey, setOverTilesByKey] = useState<Record<string, Tile[]>>({});
  const [hasMoreByKey, setHasMoreByKey] = useState<Record<string, boolean>>({});
  const [vendorNames, setVendorNames] = useState<Record<string, string>>({});
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  // picks[cat] = ["<vendorId>:<imageId>", …] in selection order (keyed by the
  // TOP-LEVEL category, not the sub-filter — a venue pick is a venue pick).
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  // imageId-keyed by full "vendorId:imageId" → the tile label + vendor (+ the
  // tile's flower species, for the florals no-repeat auto-filter), captured at
  // tap time so the "your day so far" rail survives bucket/sub-filter changes.
  const [pickedMeta, setPickedMeta] = useState<Record<string, PickMeta>>({});
  // The active sub-filter per step (venue space / dining style). undefined = all.
  const [activeSub, setActiveSub] = useState<Record<string, string | undefined>>({});
  // Florals color filter (canonical bucket names, multi-select) + venue style
  // filter — both ride the composite tile-cache key so filtered grids cache
  // independently and never collide with the unfiltered set.
  const [colorSel, setColorSel] = useState<string[]>([]);
  const [venueStyleSel, setVenueStyleSel] = useState<string | undefined>(undefined);
  // The venue style is PRESELECTED from the couple's "find my venue" décor
  // styles (brief.styles → BRIEF_STYLE_TO_VENUE_STYLE) the first time the venue
  // chips load. This ref records a manual chip tap so the preselect never
  // re-applies after the couple deselects/changes it.
  const venueStyleTouchedRef = useRef(false);
  // Food step: service style + course count persist on the BRIEF (they drive
  // generation); the active course tab + buffet narrowing chip are browse-only
  // filters. All ride the composite tile-cache key.
  const [foodService, setFoodService] = useState<"buffet" | "sit_down" | undefined>(undefined);
  const [foodCourses, setFoodCourses] = useState<3 | 4 | 5 | undefined>(undefined);
  const [foodCourse, setFoodCourse] = useState<string | undefined>(undefined);
  const [foodDining, setFoodDining] = useState<"buffet" | "food_station" | undefined>(undefined);
  // §D — dishes the couple types per course (sit-down) or per buffet slot.
  // Keyed by slot id (a course key for sit-down, `b0..b3` for buffet). Assembled
  // into brief.menu_dishes (each → one generated MENU image).
  const [dishInputs, setDishInputs] = useState<Record<string, string>>({});
  // Latest /references chip metadata per SECTION (not per composite key — chips
  // are inventory-derived from the PRE-filter set, so keeping them per section
  // stops the bar flickering away while a filtered page loads).
  const [chipMeta, setChipMeta] = useState<Record<string, RefChipMeta>>({});
  // Transient non-blocking message (florals cap refusal / species repeat warn).
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [noteOpen, setNoteOpen] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  // ── The Music step's vendor choice (DJ vs live band) ───────────────────────
  const [musicPick, setMusicPick] = useState<MusicPick | null>(null);

  // ── Resume an existing wedding (from the preview's "Edit your dream wedding") ─
  // /plan?wedding_id=… loads the saved brief and jumps past Foundations. The
  // brief (area/guests/budget/season/styles/photography) is durable; the couple
  // re-picks their looks to regenerate. GET /plan/:id is ownership-gated.
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("wedding_id");
  useEffect(() => {
    if (!resumeId || weddingId) return;
    let cancelled = false;
    void (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<{ wedding_id: string; brief: Brief; builder_selections?: unknown }>(`/plan/${resumeId}`, token);
      if (cancelled || !res.ok) return;
      const b = res.value.brief;
      setWeddingId(res.value.wedding_id);
      setBrief(b);
      if (b.area) setArea(b.area);
      if (b.guests) setGuests(b.guests);
      if (b.budget_cents) setBudget(Math.round(b.budget_cents / 100));
      if (b.season) setSeason(b.season);
      if (b.styles?.length) setStyles(b.styles);
      if (b.food_service) setFoodService(b.food_service);
      if (b.food_courses === 3 || b.food_courses === 4 || b.food_courses === 5) setFoodCourses(b.food_courses);
      if (b.menu_dishes?.length) {
        // Rebuild the per-slot inputs: sit-down dishes keep their course slot;
        // buffet dishes land in b0.. slots in order.
        const restored: Record<string, string> = {};
        let bi = 0;
        for (const d of b.menu_dishes) {
          if (b.food_service === "buffet") restored[`b${bi++}`] = d.dish;
          else restored[d.course] = d.dish;
        }
        setDishInputs(restored);
      }
      setEssentialsDone(true);
      // Foundation is its own step 0 — a resumed wedding already has its basics,
      // so jump straight into the venue step.
      setStepIdx(1);
      // Restore the saved looks (persisted SelectionV2) so resume shows the
      // ✓ states (PRD §10.1). Labels start as placeholders and are refreshed
      // from the live grid as tiles load; a pick whose image no longer exists
      // simply never re-labels (the server soft-drops it at generate).
      const sel = ((): Record<string, unknown> | null => {
        const raw = res.value.builder_selections;
        if (raw == null) return null;
        if (typeof raw === "string") { try { return JSON.parse(raw) as Record<string, unknown>; } catch { return null; } }
        return typeof raw === "object" ? (raw as Record<string, unknown>) : null;
      })();
      let restored = false;
      if (sel) {
        // Selections key → builder SECTION holding the restored ✓ (hair leads
        // the beauty pin, so a hair_makeup image pick restores on the Hair step).
        const SEL_TO_SECTION: Record<string, string> = {
          venue: "venue", florals: "florals", ceremony: "ceremony", reception: "reception",
          catering: "catering", food: "food", cake: "cake", attire_her: "attire_her", attire_him: "attire_him",
          hair_makeup: "hair",
        };
        const nextPicks: Record<string, string[]> = {};
        const nextMeta: Record<string, PickMeta> = {};
        for (const [k, entry] of Object.entries(sel)) {
          if (typeof entry === "string") {
            if (k === "dj" || k === "band") setMusicPick({ cat: k, vendorId: entry, name: "your pick" });
            continue; // legacy vendor-only lock — no image pick to restore
          }
          const section = SEL_TO_SECTION[k];
          if (!section || entry == null || typeof entry !== "object") continue;
          const e = entry as { vendor_id?: string; image_id?: string; image_ids?: string[] };
          if (!e.vendor_id) continue;
          for (const id of e.image_id ? [e.image_id] : e.image_ids ?? []) {
            const key = `${e.vendor_id}:${id}`;
            (nextPicks[section] ??= []).push(key);
            nextMeta[key] = { label: "a saved look", vendorId: e.vendor_id };
          }
        }
        if (Object.keys(nextPicks).length) { setPicks(nextPicks); setPickedMeta(nextMeta); restored = true; }
      }
      setBanner(restored
        ? "Your saved looks are loaded — adjust anything you like, then regenerate."
        : "Editing your wedding — pick your looks again to regenerate your preview.");
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, weddingId]);

  const step = STEPS[stepIdx]!;
  // Step 0 is the standalone Foundation step (no grid / picker / nav buttons —
  // it owns the EssentialsStrip and its own "continue" submit).
  const onFoundation = step.section === "foundation";

  // The locked vendor per LOCK CATEGORY = the DOMINANT (most-selected) vendor
  // across every section feeding that category, so the couple's actual
  // preference grounds the render — not whichever tile they happened to tap
  // first. Ties break toward the earliest pick. Special rules (consensus
  // design): explicit Venue-step picks own the venue lock (ceremony/reception
  // picks count only when the venue step itself has none). (The old her-first
  // attire tie rule died with the 0031 bridal_attire/groom_attire split — each
  // Look now has its own lock category.)
  const lockedVendor = useCallback((cat: string): string | null => {
    const sections = STEPS.filter((s) => s.lock === cat).map((s) => s.section);
    const sel = cat === "venue" && (picks["venue"]?.length ?? 0) > 0
      ? picks["venue"]!
      : sections.flatMap((sec) => picks[sec] ?? []);
    if (sel.length === 0) return null;
    const counts = new Map<string, number>();
    const firstSeen = new Map<string, number>();
    sel.forEach((k, i) => {
      const v = k.split(":")[0]!;
      counts.set(v, (counts.get(v) ?? 0) + 1);
      if (!firstSeen.has(v)) firstSeen.set(v, i);
    });
    return [...counts.entries()].sort((a, b) =>
      b[1] - a[1] || firstSeen.get(a[0])! - firstSeen.get(b[0])!,
    )[0]![0];
  }, [picks]);

  const vendorName = useCallback((vendorId: string): string =>
    vendorNames[vendorId] ?? "your pick", [vendorNames]);


  // ── Composite reference-key helpers ────────────────────────────────────────
  // Ceremony/Reception images come from the chosen venue vendor (req: "the
  // reception picture should be based on the vendor selected").
  const scopedVendor = useCallback(
    (s: Step): string | null => (s.scopeVenue ? lockedVendor("venue") : null),
    [lockedVendor],
  );
  // Species the couple already picked (florals) — the union of picked tiles'
  // flower_species. Drives the exclude_species= auto-filter (no repeat flowers,
  // decision 7): each pick re-fetches the grid with the union excluded.
  const pickedSpecies = useMemo(() => {
    const out: string[] = [];
    for (const k of picks["florals"] ?? []) {
      for (const sp of pickedMeta[k]?.species ?? []) if (!out.includes(sp)) out.push(sp);
    }
    return out;
  }, [picks, pickedMeta]);
  const excludeCsv = pickedSpecies.join(",");
  const colorsCsv = colorSel.join(",");

  // The Table Setting unions the locked venue's table_setting images (server-
  // side); carrying the lock in the cache key re-fetches when the venue changes.
  const tableVenueId = lockedVendor("venue");

  const keyFor = useCallback(
    (s: Step, sub?: string): string => {
      // The active filters are part of the cache key so filtered pages never
      // collide with (or mask) the unfiltered set.
      const filt = s.section === "florals" ? `c=${colorsCsv};x=${excludeCsv}`
        : s.section === "venue" ? `vs=${venueStyleSel ?? ""}`
        : s.section === "catering" ? `vid=${tableVenueId ?? ""}`
        : s.section === "food" ? `fs=${foodService ?? ""};fc=${foodCourse ?? ""};fd=${foodDining ?? ""}`
        : "";
      return [s.section, sub ?? "", scopedVendor(s) ?? "", filt].join("|");
    },
    [scopedVendor, colorsCsv, excludeCsv, venueStyleSel, tableVenueId, foodService, foodCourse, foodDining],
  );

  // ── Foundations submit → creates the wedding, seeds the brief ──────────────
  // These chips are STRUCTURED, so they're saved deterministically via /plan/brief
  // (no concierge LLM) — the basics can't fail on a transient model hiccup.
  const submitEssentials = async () => {
    if (busy) return;
    setBusy(true); setBanner(null);
    const token = await getToken();
    if (!token) { setBusy(false); return; }
    const res = await apiPost<TurnResponse>("/plan/brief", token, {
      ...(weddingId ? { wedding_id: weddingId } : {}),
      area,
      ...(guests ? { guests } : {}),
      ...(budget ? { budget_cents: budget * 100, currency: "USD" } : {}),
      ...(season ? { season } : {}),
      ...(styles.length ? { styles } : {}),
    });
    setBusy(false);
    if (!res.ok) { setBanner(`Couldn't save your basics: ${res.error.message}`); return; }
    setWeddingId(res.value.wedding_id);
    setBrief(res.value.brief);
    setEssentialsDone(true);
    // Foundation is its own step 0 — advance into the venue step once saved.
    setStepIdx((i) => (i === 0 ? 1 : i));
  };

  // ── Load reference tiles (composite-key aware, with append "load more") ─────
  // Held in a ref so the trigger effect can depend on PRIMITIVE keys (not the
  // callback identity) — avoids stale closures + redundant fetch loops. `append`
  // bypasses the cache guard so "Load more" always fetches the next page.
  // Per-composite-key budget guide returned alongside the tiles.
  const [budgetByKey, setBudgetByKey] = useState<Record<string, BudgetMeta | undefined>>({});

  const fetchTilesRef = useRef<(s: Step, append: boolean) => Promise<void>>(async () => {});
  fetchTilesRef.current = async (s: Step, append: boolean) => {
    if (!weddingId) return;
    if (s.section === "foundation") return; // no reference grid on the basics step
    const sub = activeSub[s.section];
    const key = keyFor(s, sub);
    const existing = tilesByCat[key] ?? [];
    if (!append && existing.length > 0) return; // cached initial load
    const offset = append ? existing.length : 0;
    if (!append) setLoadingKey(key);
    const token = await getToken();
    if (!token) { setLoadingKey(null); return; }
    const qs = new URLSearchParams({ category: s.cat, section: s.section, limit: "12", offset: String(offset) });
    const filter = SUB_FILTERS[s.section];
    if (sub && filter) qs.set(filter.param, sub);
    const rv = scopedVendor(s);
    if (rv) qs.set("vendor_id", rv);
    // Ceremony + Reception are STRICTLY the locked venue (the wedding norm — one
    // venue for the whole day), so drop the similar-venue fallback (§A2).
    if (s.scopeVenue && rv) qs.set("strict_venue", "1");
    // Annotation-plan filters: florals color buckets + already-picked species;
    // venue style chip (coexists with the Indoor/Outdoor space param).
    if (s.section === "florals") {
      if (colorSel.length) qs.set("colors", colorSel.join(","));
      if (pickedSpecies.length) qs.set("exclude_species", pickedSpecies.join(","));
    }
    if (s.section === "venue" && venueStyleSel) qs.set("venue_style", venueStyleSel);
    // Table Setting: pass the locked venue as the union source for its
    // table_setting images (most carousel-coherent tablescapes).
    if (s.section === "catering" && tableVenueId) qs.set("venue_id", tableVenueId);
    // Food: service style (buffet ⊇ stations) + the active course tab + the
    // optional buffet narrowing chip.
    if (s.section === "food") {
      if (foodService) qs.set("service", foodService);
      if (foodService === "sit_down" && foodCourse) qs.set("course", foodCourse);
      if (foodService === "buffet" && foodDining) qs.set("dining", foodDining);
    }
    const res = await apiGet<{
      tiles: Tile[]; has_more: boolean; budget?: BudgetMeta;
      over_budget_tiles?: Tile[];
      color_chips?: ColorChip[]; venue_style_chips?: VenueStyleChip[]; colors_applied?: string[];
    }>(`/plan/${weddingId}/references?${qs.toString()}`, token);
    setLoadingKey(null);
    const tiles = res.ok ? res.value.tiles : [];
    // The venue step's "out of budget" group (venues over the venue cap) — not
    // paginated; replaced wholesale on each fresh (non-append) fetch.
    const overTiles = res.ok ? res.value.over_budget_tiles ?? [] : [];
    if (!append) setOverTilesByKey((o) => ({ ...o, [key]: overTiles }));
    setHasMoreByKey((h) => ({ ...h, [key]: res.ok ? res.value.has_more : false }));
    setBudgetByKey((b) => ({ ...b, [key]: res.ok ? res.value.budget : undefined }));
    if (res.ok) setChipMeta((c) => ({
      ...c,
      // Replace the section's meta wholesale so a stale colors_applied from a
      // previous filtered fetch never lingers.
      [s.section]: {
        ...(res.value.color_chips ? { color_chips: res.value.color_chips } : {}),
        ...(res.value.venue_style_chips ? { venue_style_chips: res.value.venue_style_chips } : {}),
        ...(res.value.colors_applied ? { colors_applied: res.value.colors_applied } : {}),
      },
    }));
    // Refresh restored-pick placeholders (and capture species + cost for the
    // exclude_species filter and the running tally) once the real tiles are in
    // view — covers both the main grid and the venue over-budget group.
    const allFetched = [...tiles, ...overTiles];
    if (allFetched.length) setPickedMeta((m) => {
      let changed = false;
      const next = { ...m };
      for (const t of allFetched) {
        const k = `${t.vendor_id}:${t.image_id}`;
        const cur = next[k];
        if (cur && (cur.label !== t.label || cur.species === undefined || cur.costLowCents === undefined)) {
          next[k] = {
            label: t.label, vendorId: t.vendor_id, species: t.flower_species ?? [],
            costLowCents: t.cost_low_cents, costHighCents: t.cost_high_cents,
          };
          changed = true;
        }
      }
      return changed ? next : m;
    });
    setTilesByCat((m) => {
      if (!append) return { ...m, [key]: tiles };
      const seen = new Set((m[key] ?? []).map((t) => t.image_id));
      const merged = [...(m[key] ?? []), ...tiles.filter((t) => !seen.has(t.image_id))];
      return { ...m, [key]: merged };
    });
    if (tiles.length) setVendorNames((n) => {
      const next = { ...n };
      for (const t of tiles) if (t.vendor_name) next[t.vendor_id] = t.vendor_name;
      return next;
    });
  };

  // Re-fetch whenever the step, its active sub-filter, the scoped venue, OR an
  // annotation filter (florals colors / excluded species, venue style) changes
  // (primitive deps — the composite key carries all of them).
  const stepSub = activeSub[step.section];
  const stepScopedVendor = scopedVendor(step);
  useEffect(() => {
    if (essentialsDone && weddingId) void fetchTilesRef.current(step, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [essentialsDone, weddingId, step.section, stepSub, stepScopedVendor, colorsCsv, excludeCsv, venueStyleSel, tableVenueId, foodService, foodCourse, foodDining]);

  // Preselect the venue style from the couple's "find my venue" décor styles:
  // map brief.styles → candidate venue_styles, and pick the FIRST that actually
  // has local inventory (chips are count-desc sorted, so this is the strongest
  // available match). One-shot — skipped once the couple taps a style chip, and
  // never fires while a style is already chosen (incl. a resumed wedding).
  const venueStyleChips = chipMeta["venue"]?.venue_style_chips;
  useEffect(() => {
    if (venueStyleTouchedRef.current || venueStyleSel !== undefined) return;
    if (!venueStyleChips?.length || !styles.length) return;
    const candidates = new Set<string>(styles.flatMap((s) => BRIEF_STYLE_TO_VENUE_STYLE[s] ?? []));
    const match = venueStyleChips.find((c) => candidates.has(c.style));
    if (match) setVenueStyleSel(match.style);
  }, [venueStyleChips, styles, venueStyleSel]);

  const currentKey = keyFor(step, stepSub);

  // ── Persist the couple's intent for a section, then advance ────────────────
  const SECTION_PHRASE: Record<string, string> = {
    attire_her: "her look", attire_him: "his look",
  };
  const persistAndNext = async () => {
    if (busy || !weddingId) return;
    const sec = step.section;
    const note = (notes[sec] ?? "").trim();
    const lv = step.lock ? lockedVendor(step.lock) : null;
    const pieces: string[] = [];
    if (lv) pieces.push(`For ${SECTION_PHRASE[sec] ?? step.title.toLowerCase()}, I love ${vendorName(lv)}'s look`);
    // (The menu — service style + courses — persists STRUCTURALLY via
    // POST /plan/brief in pickFoodService/pickFoodCourses, never as free text:
    // a flaky /plan/turn rewrite could drop just-saved structured fields.)
    if (note) pieces.push(note);
    if (pieces.length) {
      setBusy(true);
      const token = await getToken();
      if (token) {
        const res = await apiPost<TurnResponse>("/plan/turn", token, { message: pieces.join(". ") + ".", wedding_id: weddingId });
        if (res.ok) setBrief(res.value.brief);
      }
      setBusy(false);
    }
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    else setPhase("review");
  };

  // Persist the photography style structurally (drives generation globally).
  const pickPhotoStyle = async (styleVal: string) => {
    if (!weddingId) return;
    setBrief((b) => ({ ...b, photography_style: styleVal })); // optimistic
    const token = await getToken();
    if (!token) return;
    const res = await apiPost<TurnResponse>("/plan/brief", token, { wedding_id: weddingId, photography_style: styleVal });
    if (res.ok) setBrief(res.value.brief);
  };

  // Persist the Food step's service style / course count structurally — they
  // gate the Feast scene and shape the Tablescape/Reception framings.
  const pickFoodService = async (v: "buffet" | "sit_down") => {
    setFoodService(v);
    if (v === "buffet") setFoodCourse(undefined);
    else setFoodDining(undefined);
    setBrief((b) => ({ ...b, food_service: v })); // optimistic
    if (!weddingId) return;
    const token = await getToken();
    if (!token) return;
    const res = await apiPost<TurnResponse>("/plan/brief", token, { wedding_id: weddingId, food_service: v });
    if (res.ok) setBrief(res.value.brief);
  };
  const pickFoodCourses = async (n: 3 | 4 | 5) => {
    setFoodCourses(n);
    // Drop the active tab if the new plan no longer includes that course.
    setFoodCourse((c) => (c && (COURSE_SETS[n] as readonly string[]).includes(c) ? c : undefined));
    setBrief((b) => ({ ...b, food_courses: n })); // optimistic
    if (!weddingId) return;
    const token = await getToken();
    if (!token) return;
    const res = await apiPost<TurnResponse>("/plan/brief", token, { wedding_id: weddingId, food_courses: n });
    if (res.ok) setBrief(res.value.brief);
  };
  // Assemble menu_dishes from the per-slot inputs. Sit-down slots are course
  // keys; buffet slots (`b0..`) default to the entrée course for framing.
  const buildMenuDishes = useCallback(
    (inputs: Record<string, string>): { course: string; dish: string }[] =>
      Object.entries(inputs)
        .map(([slot, dish]) => ({ course: slot.startsWith("b") ? "entree" : slot, dish: dish.trim() }))
        .filter((d) => d.dish.length > 0)
        .slice(0, 6),
    [],
  );
  const commitDishes = useCallback(async (inputs: Record<string, string>) => {
    const menu_dishes = buildMenuDishes(inputs);
    setBrief((b) => ({ ...b, menu_dishes })); // optimistic
    if (!weddingId) return;
    const token = await getToken();
    if (!token) return;
    const res = await apiPost<TurnResponse>("/plan/brief", token, { wedding_id: weddingId, menu_dishes });
    if (res.ok) setBrief(res.value.brief);
  }, [buildMenuDishes, weddingId, getToken]);

  // Selection rules (annotation plan §7.1): radio everywhere (tapping another
  // tile SWAPS the pick; tapping the current one deselects); florals multi-pick
  // capped at 3 (a 4th tap is a no-op + toast).
  const togglePick = (cat: string, t: Tile) => {
    const key = `${t.vendor_id}:${t.image_id}`;
    const max = STEPS.find((s) => s.section === cat)?.maxPicks ?? 1;
    const cur = picks[cat] ?? [];
    const isOn = cur.includes(key);
    if (!isOn && max > 1 && cur.length >= max) {
      showToast(`That's the limit — ${max} floral looks. Tap one to swap it.`);
      return;
    }
    // A florals tile without species data can't be deduped against earlier
    // picks — warn (non-blocking; the pick still registers, the server dedupes
    // species again at generate).
    if (!isOn && cat === "florals" && cur.length > 0 && (t.flower_species?.length ?? 0) === 0) {
      showToast("This one may repeat a flower type you've picked.");
    }
    setPickedMeta((m) => ({ ...m, [key]: {
      label: t.label, vendorId: t.vendor_id, species: t.flower_species ?? [],
      costLowCents: t.cost_low_cents, costHighCents: t.cost_high_cents,
    } }));
    setPicks((p) => {
      const prev = p[cat] ?? [];
      if (prev.includes(key)) return { ...p, [cat]: prev.filter((k) => k !== key) }; // tap current = deselect
      if (max === 1) return { ...p, [cat]: [key] };                                  // radio: replace
      return prev.length < max ? { ...p, [cat]: [...prev, key] } : p;                // florals: cap
    });
  };

  // Distinct picked-tile LABELS per category for the "your day so far" rail
  // (req: show the TYPE of pictures selected, not the vendor name).
  const pickedLabels = useCallback((cat: string): string[] => {
    const out: string[] = [];
    for (const k of picks[cat] ?? []) {
      const lbl = pickedMeta[k]?.label;
      if (lbl && !out.includes(lbl)) out.push(lbl);
    }
    return out;
  }, [picks, pickedMeta]);

  // ── Running cost tally (consensus design) ──────────────────────────────────
  // ONE cost per LOCK category (dedup shared pools: venue ⊇ ceremony/reception,
  // catering ⊇ table/food, hair_makeup ⊇ hair/nails/makeup) via the dominant
  // locked vendor's captured cost. Photography books nothing (excluded); the
  // music vendor IS counted (the preview breakdown costs it too). Vendors with
  // no price signal count toward `unpriced` so the bar never silently undercounts.
  const budgetCents = brief.budget_cents ?? (budget ? budget * 100 : null);
  const tally = useMemo(() => {
    const costByVendor = new Map<string, { low: number; high: number }>();
    for (const m of Object.values(pickedMeta)) {
      if (m.costLowCents != null && m.costHighCents != null) {
        costByVendor.set(m.vendorId, { low: m.costLowCents, high: m.costHighCents });
      }
    }
    const lockCats = [...new Set(STEPS.flatMap((s) => (s.lock ? [s.lock] : [])))];
    let low = 0, high = 0, priced = 0, unpriced = 0;
    for (const cat of lockCats) {
      const v = lockedVendor(cat);
      if (!v) continue;
      const c = costByVendor.get(v);
      if (c) { low += c.low; high += c.high; priced++; } else unpriced++;
    }
    if (musicPick) {
      if (musicPick.costLowCents != null && musicPick.costHighCents != null) {
        low += musicPick.costLowCents; high += musicPick.costHighCents; priced++;
      } else unpriced++;
    }
    return { low, high, priced, unpriced };
  }, [pickedMeta, musicPick, lockedVendor]);

  // ── Readiness (Phase B) ────────────────────────────────────────────────────
  const hasStyle = (brief.styles?.length ?? 0) > 0 || (brief.palette?.length ?? 0) > 0 || styles.length > 0;
  const venueChosen = Boolean(lockedVendor("venue"));
  const canGenerate = venueChosen && hasStyle;

  // ── Generate → composing ───────────────────────────────────────────────────
  const generate = async () => {
    if (!weddingId || busy) return;
    setBusy(true); setBanner(null);
    const token = await getToken();
    if (!token) { setBusy(false); return; }
    // Build SelectionV2 entries (annotation plan §5.1/§7.1): every entry carries
    // the picked vendor + the CHOSEN reference image — single image_id for
    // radio sections, image_ids (pick-ordered, ≤3) for florals, legacy string
    // for vendor-only locks (music, or a lock inferred without an image pick).
    const splitKey = (k: string): { vendorId: string; imageId: string } => {
      const i = k.indexOf(":");
      return { vendorId: k.slice(0, i), imageId: k.slice(i + 1) };
    };
    const firstPick = (section: string): { vendorId: string; imageId: string } | null => {
      const k = (picks[section] ?? [])[0];
      return k ? splitKey(k) : null;
    };
    const selections: Record<string, SelectionEntry> = {};
    // Venue: the Venue step's own pick is THE generation reference; ceremony/
    // reception ride as their OWN keys (the server maps them to scene pins).
    // Venue / Ceremony / Reception are multi-pick (≤4): each picked SPACE becomes
    // its own output image (the multi-space fan-out, §F). All picks share the
    // locked venue vendor (the grid is strict-scoped), so vendor_id = the first
    // pick's vendor; image_ids keep pick order ([0] = lead / the anchor space).
    const pinEntry = (section: string): SelectionEntry | null => {
      const ps = (picks[section] ?? []).map(splitKey);
      if (ps.length === 0) return null;
      return ps.length === 1
        ? { vendor_id: ps[0]!.vendorId, image_id: ps[0]!.imageId }
        : { vendor_id: ps[0]!.vendorId, image_ids: ps.map((p) => p.imageId) };
    };
    const venueEntry = pinEntry("venue");
    if (venueEntry) selections["venue"] = venueEntry;
    else { const v = lockedVendor("venue"); if (v) selections["venue"] = v; }
    for (const sec of ["ceremony", "reception"] as const) {
      const e = pinEntry(sec);
      if (e) selections[sec] = e;
    }
    // Florals: multi-pick — picks may span vendors; vendor_id = the FIRST
    // pick's vendor (the lock), image_ids keep pick order ([0] = lead).
    const floral = (picks["florals"] ?? []).map(splitKey);
    if (floral.length) selections["florals"] = { vendor_id: floral[0]!.vendorId, image_ids: floral.map((p) => p.imageId) };
    // Table Setting pins the Tablescape scene; Food pins the Feast scene (the
    // server guards the catering vendor LOCK to true caterers — a venue-vendor
    // table pick still pins its image).
    for (const sec of ["catering", "food", "cake"] as const) {
      const p = firstPick(sec);
      if (p) selections[sec] = { vendor_id: p.vendorId, image_id: p.imageId };
    }
    // Attire (0031 split): each Look carries its OWN category lock plus the
    // per-scene pick — Her Look grounds on a bridal_attire vendor, His Look on
    // a groom_attire vendor, by construction.
    const bridalLock = lockedVendor("bridal_attire");
    if (bridalLock) selections["bridal_attire"] = bridalLock;
    const groomLock = lockedVendor("groom_attire");
    if (groomLock) selections["groom_attire"] = groomLock;
    for (const sceneKey of ["attire_her", "attire_him"] as const) {
      const p = firstPick(sceneKey);
      if (p) selections[sceneKey] = { vendor_id: p.vendorId, image_id: p.imageId };
    }
    // Beauty: the hair pick leads the hair_makeup pin (the standalone Makeup step
    // was removed; the bride's makeup is held constant via the StyleSheet
    // heuristic). The vendor lock falls back to dominance (which includes nails).
    const beautyPick = firstPick("hair");
    if (beautyPick) selections["hair_makeup"] = { vendor_id: beautyPick.vendorId, image_id: beautyPick.imageId };
    else { const hm = lockedVendor("hair_makeup"); if (hm) selections["hair_makeup"] = hm; }
    // Nails: a picked nail reference pins the dedicated Nails scene (the server's
    // requiresPin gate — absent, nails stay only a framing detail in the portrait).
    const nailsPick = firstPick("nails");
    if (nailsPick) selections["nails"] = { vendor_id: nailsPick.vendorId, image_id: nailsPick.imageId };
    // Music keeps the legacy string form (a vendor choice — no reference image).
    if (musicPick) selections[musicPick.cat] = musicPick.vendorId;
    // Persist the picked TAXONOMY categories on the brief (deterministic,
    // zero-LLM) so planScenes appends the matching non-core scenes (Her/His
    // Look, Bridal portrait…). Scene keys (ceremony/reception/attire_her/
    // attire_him) never belong on the brief.
    const lockCats = [...new Set(STEPS.flatMap((s) => (s.lock ? [s.lock] : [])))];
    const pickedCats = lockCats.filter((c) => lockedVendor(c) != null);
    if (musicPick) pickedCats.push(musicPick.cat);
    // Persist categories + any typed dishes (in case the user clicks Generate
    // before a dish input blurred) in one deterministic brief PATCH.
    const menu_dishes = buildMenuDishes(dishInputs);
    if (pickedCats.length || menu_dishes.length) {
      await apiPost<TurnResponse>("/plan/brief", token, {
        wedding_id: weddingId,
        ...(pickedCats.length ? { categories: pickedCats } : {}),
        ...(menu_dishes.length ? { menu_dishes } : {}),
      });
    }
    try {
      const res = await fetch(`${API_BASE()}/plan/${weddingId}/generate`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ selections }),
      });
      const body = await res.json().catch(() => ({})) as {
        job_id?: string; need_more_info?: boolean; question?: string; error?: { message?: string };
      };
      setBusy(false);
      if (res.status === 422 && body.need_more_info) {
        // Drop back onto the venue step (1) — foundation (0) is already done.
        setPhase("builder"); setStepIdx(1);
        setBanner(body.question ?? "Tell me a little more so I can pick your venue.");
        return;
      }
      if (!res.ok || !body.job_id) { setBanner(`Couldn't start: ${body.error?.message ?? res.status}`); return; }
      setJobId(body.job_id);
      setPhase("composing");
    } catch (e) {
      setBusy(false); setBanner(`Couldn't start: ${String(e)}`);
    }
  };

  const [jobId, setJobId] = useState<string | null>(null);

  if (phase === "composing" && jobId) {
    return (
      <ComposingOverlay
        jobId={jobId}
        weddingId={weddingId}
        getToken={stableGetToken}
        onReveal={() => navigate(`/vision/${jobId}`)}
        onError={(msg) => { setPhase("review"); setBanner(msg); }}
      />
    );
  }

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h2 className="font-display text-3xl">Build your dream wedding</h2>
        <p className="mt-1 text-stone-600">Assemble your day one piece at a time — starting with the venue. Pick the looks you love, tell me what you're picturing, and I'll compose the whole day.</p>
      </header>

      {banner && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">{banner}</div>
      )}

      {/* Transient non-blocking toast (florals cap / species-repeat warning). */}
      {toast && (
        <div aria-live="polite" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full bg-stone-900/90 text-stone-50 text-sm px-4 py-2" style={{ boxShadow: "var(--bp-shadow-card)" }}>
          {toast}
        </div>
      )}

      {phase === "review" ? (
        <ReviewCard
          brief={brief} styles={styles}
          venueName={venueChosen ? vendorName(lockedVendor("venue")!) : null}
          floralsName={lockedVendor("florals") ? vendorName(lockedVendor("florals")!) : null}
          guests={guests} budget={budget} season={season}
          canGenerate={canGenerate} busy={busy}
          onBack={() => setPhase("builder")}
          onGenerate={generate}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_220px] gap-6">
          {/* Step rail — navigable build */}
          <nav className="hidden lg:block">
            <ol className="space-y-1">
              {STEPS.map((s, i) => {
                const done = s.section === "foundation" ? essentialsDone
                  : s.picker === "photo" ? Boolean(brief.photography_style)
                  : s.picker === "music" ? Boolean(musicPick)
                  : (picks[s.section]?.length ?? 0) > 0;
                const active = i === stepIdx;
                // The foundation step is always reachable; the rest unlock once
                // the basics are saved.
                const reachable = s.section === "foundation" || essentialsDone;
                return (
                  <li key={s.section}>
                    <button
                      onClick={() => reachable && setStepIdx(i)}
                      disabled={!reachable}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${active ? "bg-stone-900 text-stone-50" : "hover:bg-stone-100 text-stone-700"} disabled:opacity-40`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${done ? "bg-emerald-600 text-white" : active ? "bg-stone-50 text-stone-900" : "bg-stone-200 text-stone-600"}`}>
                          {done ? "✓" : i + 1}
                        </span>
                        <span className="font-medium">{s.title}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
            {essentialsDone && (
              <button onClick={() => setPhase("review")} disabled={!canGenerate}
                className="mt-4 w-full px-3 py-2 rounded-lg text-sm bg-[var(--bp-accent)] text-white hover:opacity-90 disabled:opacity-40">
                Review & generate →
              </button>
            )}
          </nav>

          {/* Stage */}
          <div>
            <div className="text-xs uppercase tracking-wide text-[var(--bp-accent)] font-medium">{step.lead}</div>
            <h3 className="font-display text-2xl mt-1">{step.title}</h3>
            <p className="text-stone-600 mt-1 mb-4">{step.q}</p>

            {/* Foundations — its OWN step 0 (no grid); submit seeds the brief
                and advances to the venue. */}
            {onFoundation && (
              <EssentialsStrip
                done={essentialsDone}
                area={area} setArea={setArea}
                guests={guests} setGuests={setGuests}
                budget={budget} setBudget={setBudget}
                season={season} setSeason={setSeason}
                styles={styles} setStyles={setStyles}
                busy={busy} onSubmit={submitEssentials}
              />
            )}

            {/* The Look step is a photography-STYLE picker, The Music a DJ/band
                vendor picker — neither is an image grid. */}
            {!onFoundation && essentialsDone && step.picker === "photo" ? (
              <>
                <PhotoStylePicker selected={brief.photography_style} onPick={pickPhotoStyle} />
                <p className="mt-3 text-xs text-stone-500">
                  This sets how your generated pictures look — every image in your preview is rendered in this style. It doesn't book a photographer.
                </p>
              </>
            ) : !onFoundation && essentialsDone && step.picker === "music" ? (
              <MusicPicker
                weddingId={weddingId}
                getToken={stableGetToken}
                pick={musicPick}
                onPick={setMusicPick}
              />
            ) : !onFoundation && essentialsDone && (
              <>
                <p className="text-xs text-stone-500 mb-3">
                  {(step.maxPicks ?? 1) > 1
                    ? <>Pick up to {step.maxPicks} — I'll combine the varieties (no repeats). </>
                    : <>Tap the look you love — tapping another swaps it. </>}
                  You're choosing a <em>style</em> we'll use to render your day, not booking anything.
                </p>

                {/* Per-section budget guide (server-computed share of the total). */}
                {budgetByKey[currentKey] && (
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-stone-100 border border-stone-200 px-3 py-1 text-stone-700">
                      Budget guide: ~{dollars(budgetByKey[currentKey]!.allocated_cents)} · showing looks up to ~{dollars(budgetByKey[currentKey]!.cap_cents)}
                    </span>
                    {budgetByKey[currentKey]!.relaxed && (
                      <span className="text-amber-700">Few options fit this part of your budget — some shown are a stretch.</span>
                    )}
                  </div>
                )}

                {/* Scope hint for the venue-anchored sections. */}
                {step.scopeVenue && (
                  <p className="text-xs text-stone-500 mb-3">
                    {stepScopedVendor
                      ? `Showing ${step.title.toLowerCase()} settings at ${vendorName(stepScopedVendor)} — your ceremony, dining and reception all happen at your one venue.`
                      : "Pick a venue first and I'll lead with your venue's settings — for now, here are looks from venues that match your day."}
                  </p>
                )}

                {/* Venue space sub-filters */}
                {SUB_FILTERS[step.section] && (
                  <SubFilterBar
                    label="Show me"
                    options={SUB_FILTERS[step.section]!.opts}
                    value={stepSub}
                    onPick={(v) =>
                      setActiveSub((s) => ({ ...s, [step.section]: s[step.section] === v ? undefined : v }))
                    }
                    disabledHint={undefined}
                    venueChosen={venueChosen}
                  />
                )}

                {/* The Food step's menu bar: service style → buffet options or
                    course count + course tabs (the grid follows the active tab). */}
                {step.section === "food" && (
                  <FoodMenuBar
                    service={foodService} onService={(v) => void pickFoodService(v)}
                    dining={foodDining} onDining={(v) => setFoodDining((d) => (d === v ? undefined : v))}
                    courses={foodCourses} onCourses={(n) => void pickFoodCourses(n)}
                    course={foodCourse} onCourse={(v) => setFoodCourse((c) => (c === v ? undefined : v))}
                    dishInputs={dishInputs}
                    onDishChange={(slot, value) => setDishInputs((d) => ({ ...d, [slot]: value }))}
                    onDishCommit={() => void commitDishes(dishInputs)}
                  />
                )}

                {/* Venue STYLE chips (Garden/Ballroom/…) — inventory-derived,
                    coexist with the Indoor/Outdoor row above. */}
                {step.section === "venue" && (chipMeta["venue"]?.venue_style_chips?.length ?? 0) > 0 && (
                  <SubFilterBar
                    label="Style"
                    options={chipMeta["venue"]!.venue_style_chips!.map((c) => ({ v: c.style, l: c.label }))}
                    value={venueStyleSel}
                    onPick={(v) => { venueStyleTouchedRef.current = true; setVenueStyleSel((s) => (s === v ? undefined : v)); }}
                    disabledHint={undefined}
                    venueChosen={venueChosen}
                  />
                )}

                {/* Flower-color filter — only buckets with local inventory render. */}
                {step.section === "florals" && (chipMeta["florals"]?.color_chips?.length ?? 0) > 0 && (
                  <ColorChipBar
                    chips={chipMeta["florals"]!.color_chips!}
                    selected={colorSel}
                    applied={loadingKey === currentKey ? undefined : chipMeta["florals"]?.colors_applied}
                    onToggle={(b) => setColorSel((c) => (c.includes(b) ? c.filter((x) => x !== b) : [...c, b]))}
                    onClear={() => setColorSel([])}
                  />
                )}

                {/* Species auto-filter explainer — the grid hides flowers that
                    fully repeat an earlier pick's species. */}
                {step.section === "florals" && pickedSpecies.length > 0 && (
                  <p className="mb-3">
                    <span className="inline-flex items-center rounded-full bg-stone-100 border border-stone-200 px-3 py-1 text-xs text-stone-700">
                      Showing flowers different from your {pickedSpecies.slice(0, 2).map(titleize).join(" & ")} pick{(picks["florals"]?.length ?? 0) > 1 ? "s" : ""}.
                    </span>
                  </p>
                )}

                <ReferenceGrid
                  cat={step.section}
                  loading={loadingKey === currentKey}
                  tiles={tilesByCat[currentKey] ?? []}
                  picked={new Set(picks[step.section] ?? [])}
                  onToggle={togglePick}
                  getToken={stableGetToken}
                  emptyHint={
                    step.section === "florals" && colorSel.length > 0
                      ? "No florals match those colors nearby — tap “Clear colors” above to see everything."
                      : (budgetByKey[currentKey]?.excluded_over_budget ?? 0) > 0
                        ? `${EMPTY_HINTS[step.section] ?? "No local references for this yet."} Some options were outside your budget guide.`
                        : EMPTY_HINTS[step.section]
                  }
                />

                {hasMoreByKey[currentKey] && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => void fetchTilesRef.current(step, true)}
                      disabled={loadingKey === currentKey}
                      className="px-5 py-2 rounded-full border border-stone-300 text-sm hover:bg-stone-100 disabled:opacity-40"
                    >
                      Load more pictures
                    </button>
                  </div>
                )}

                {/* "Out of budget" — vendors whose cheapest offering exceeds this
                    category's budget guide (every budgeted section now). Browsable
                    + selectable (the couple may stretch); picking one pushes the
                    running tally over. */}
                {(overTilesByKey[currentKey]?.length ?? 0) > 0 && (
                  <details className="mt-6 rounded-xl border border-amber-200 bg-amber-50/40">
                    <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-amber-900 select-none">
                      Out of budget · {overTilesByKey[currentKey]!.length} option{overTilesByKey[currentKey]!.length > 1 ? "s" : ""} over your budget
                      <span className="ml-1 font-normal text-amber-700">— browse if you'd like to stretch</span>
                    </summary>
                    <div className="px-4 pb-4 pt-1">
                      <ReferenceGrid
                        cat={step.section}
                        loading={false}
                        tiles={overTilesByKey[currentKey]!}
                        picked={new Set(picks[step.section] ?? [])}
                        onToggle={togglePick}
                        getToken={stableGetToken}
                      />
                    </div>
                  </details>
                )}
              </>
            )}

            {/* Mobile-only running tally (the rail with the full tally is below
                the fold on small screens). */}
            {!onFoundation && essentialsDone && (tally.low > 0 || tally.high > 0) && (
              <div className="lg:hidden mt-5">
                <BudgetTally low={tally.low} high={tally.high} budgetCents={budgetCents} unpriced={tally.unpriced} />
              </div>
            )}

            {/* Free text + nav */}
            {!onFoundation && essentialsDone && (
              <div className="mt-5">
                {noteOpen[step.section] ? (
                  <textarea
                    value={notes[step.section] ?? ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [step.section]: e.target.value }))}
                    placeholder={`Describe what you're picturing for ${step.title.toLowerCase()}…`}
                    rows={3}
                    className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                  />
                ) : (
                  <button onClick={() => setNoteOpen((o) => ({ ...o, [step.section]: true }))}
                    className="text-sm text-stone-500 underline underline-offset-4 hover:text-stone-800">
                    + Describe it in your words
                  </button>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <button
                    onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
                    disabled={stepIdx === 0}
                    className="px-4 py-2 rounded-full border border-stone-300 text-sm hover:bg-stone-100 disabled:opacity-30"
                  >← Back</button>
                  <div className="flex gap-2">
                    {!step.gating && (
                      <button onClick={persistAndNext} disabled={busy}
                        className="px-4 py-2 rounded-full text-sm text-stone-500 hover:text-stone-800 disabled:opacity-40">
                        Skip
                      </button>
                    )}
                    <button onClick={persistAndNext} disabled={busy || (step.gating && !venueChosen)}
                      className="px-5 py-2 rounded-full bg-stone-900 text-stone-50 text-sm hover:bg-stone-700 disabled:opacity-40">
                      {busy ? "Saving…" : stepIdx < STEPS.length - 1 ? "Next →" : "Review →"}
                    </button>
                  </div>
                </div>
                {step.gating && !venueChosen && (
                  <p className="mt-2 text-xs text-stone-500">Pick at least one venue to continue — it anchors every other picture.</p>
                )}
              </div>
            )}
          </div>

          {/* Captured details rail */}
          <aside className="lg:border-l lg:border-stone-200 lg:pl-5">
            {/* Running estimated-cost tally vs the couple's budget (updates as
                they pick; sums one cost per locked category). */}
            <div className="mb-4">
              <BudgetTally low={tally.low} high={tally.high} budgetCents={budgetCents} unpriced={tally.unpriced} />
            </div>
            <div className="text-xs uppercase tracking-wide text-stone-400 font-medium mb-2">Your day so far</div>
            <dl className="space-y-2 text-sm">
              <Detail label="Where" value={brief.area ?? area} />
              <Detail label="Guests" value={brief.guests ? String(brief.guests) : guests ? String(guests) : "—"} />
              <Detail label="Budget" value={dollars(brief.budget_cents ?? (budget ? budget * 100 : undefined))} />
              <Detail label="Season" value={brief.season ?? season ?? "—"} />
              <Detail label="Style" value={(brief.styles?.length ? brief.styles : styles).join(", ") || "—"} />
              {foodService && (
                <Detail label="Menu" value={foodService === "buffet" ? "Buffet" : `Sit-down · ${foodCourses ?? 3} courses`} />
              )}
            </dl>
            <div className="mt-4 space-y-2">
              {STEPS.map((s) => {
                // The Look step records a photography style; The Music a DJ/band
                // choice — not picture tiles.
                if (s.picker === "photo") {
                  const ps = brief.photography_style;
                  if (!ps) return null;
                  const lbl = PHOTO_STYLES.find((p) => p.v === ps)?.l ?? ps;
                  return (
                    <div key={s.section} className="flex items-center gap-2 text-sm">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <span className="text-stone-500">{s.title.replace(/^The /, "")}:</span>
                      <span className="font-medium truncate">{lbl}</span>
                    </div>
                  );
                }
                if (s.picker === "music") {
                  if (!musicPick) return null;
                  return (
                    <div key={s.section} className="flex items-center gap-2 text-sm">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <span className="text-stone-500">Music:</span>
                      <span className="font-medium truncate">{musicPick.cat === "dj" ? "DJ" : "Live band"} · {musicPick.name}</span>
                    </div>
                  );
                }
                const sectionPicks = picks[s.section] ?? [];
                if (sectionPicks.length === 0) return null;
                const labels = pickedLabels(s.section);
                // The couple's request: show the SELECTED IMAGES per category, not
                // just text. Thumbnails of each picked reference (≤4); the caption
                // keeps the content labels; tapping jumps back to that step.
                const imageIds = sectionPicks.map((k) => k.slice(k.indexOf(":") + 1)).filter(Boolean).slice(0, 4);
                const stepIndex = STEPS.findIndex((x) => x.section === s.section);
                return (
                  <button
                    key={s.section}
                    type="button"
                    onClick={() => stepIndex >= 0 && setStepIdx(stepIndex)}
                    className="w-full text-left rounded-lg hover:bg-stone-50 px-1.5 py-1.5 -mx-1.5 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                      <span className="text-stone-500 shrink-0">{s.title.replace(/^The /, "")}</span>
                      {labels.length > 0 && (
                        <span className="font-medium text-stone-700 truncate">{labels.slice(0, 2).join(" · ")}{labels.length > 2 ? ` +${labels.length - 2}` : ""}</span>
                      )}
                    </div>
                    <div className="mt-1.5 flex gap-1.5 pl-3.5">
                      {imageIds.map((id) => (
                        <AuthedImage
                          key={id}
                          src={`${API_BASE()}/admin/image/${id}`}
                          alt={s.title}
                          getToken={stableGetToken}
                          className="h-12 w-12 rounded-md object-cover border border-stone-200"
                          placeholderClassName="h-12 w-12 rounded-md"
                        />
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-stone-400">{label}</dt>
      <dd className="text-stone-800 text-right truncate">{value}</dd>
    </div>
  );
}

// ─── Running cost tally bar ──────────────────────────────────────────────────
// Mirrors Vision.tsx's BudgetBar treatment (estimated range, target, progress
// bar, friendly over/under message). `unpriced` = picked categories with no
// price signal — surfaced so the estimate never silently undercounts.
function BudgetTally({ low, high, budgetCents, unpriced }: {
  low: number; high: number; budgetCents: number | null; unpriced: number;
}) {
  if (low === 0 && high === 0) {
    return (
      <div className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-xs text-stone-500">
        As you pick looks, I'll tally the estimated cost here{budgetCents ? ` against your ${dollars(budgetCents)} budget` : ""}.
      </div>
    );
  }
  const mid = (low + high) / 2;
  const hasBudget = budgetCents != null && budgetCents > 0;
  const pct = hasBudget ? Math.min(100, Math.round((mid / budgetCents) * 100)) : 0;
  const over = hasBudget && mid > budgetCents * 1.05;
  const under = hasBudget && mid < budgetCents * 0.95;
  const barColor = !hasBudget ? "bg-stone-400" : over ? "bg-[#b4541f]" : under ? "bg-emerald-500" : "bg-[var(--bp-accent)]";
  const delta = hasBudget ? Math.abs(mid - budgetCents) : 0;
  const msg = !hasBudget
    ? "Add a budget in your basics to track against it."
    : over ? `About ${dollars(delta)} over your ${dollars(budgetCents)} budget so far.`
    : under ? `About ${dollars(delta)} under your ${dollars(budgetCents)} budget — room to spare.`
    : `Right around your ${dollars(budgetCents)} budget.`;
  return (
    <div className="rounded-xl border border-stone-200 bg-white px-4 py-3" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-stone-400 font-medium">Estimated so far</span>
        <span className="text-base font-semibold text-stone-800">{range(low, high)}</span>
      </div>
      {hasBudget && (
        <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${Math.max(4, pct)}%` }} />
        </div>
      )}
      <p className={`mt-1.5 text-xs ${over ? "text-[#b4541f]" : "text-stone-500"}`}>{msg}</p>
      {unpriced > 0 && (
        <p className="mt-1 text-[11px] text-stone-400">+{unpriced} pick{unpriced > 1 ? "s" : ""} we couldn't price yet</p>
      )}
    </div>
  );
}

// ─── Foundations strip ───────────────────────────────────────────────────────
function EssentialsStrip(props: {
  done: boolean;
  area: string; setArea: (v: string) => void;
  guests: number | null; setGuests: (v: number) => void;
  budget: number | null; setBudget: (v: number) => void;
  season: string | null; setSeason: (v: string) => void;
  styles: string[]; setStyles: (v: string[]) => void;
  busy: boolean; onSubmit: () => void;
}) {
  const { done } = props;
  const toggleStyle = (s: string) =>
    props.setStyles(
      props.styles.includes(s)
        ? props.styles.filter((x) => x !== s)
        : props.styles.length >= MAX_STYLES ? props.styles : [...props.styles, s],
    );
  return (
    <div className="mb-6 rounded-2xl border border-stone-200 bg-white p-5" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      <p className="text-sm text-stone-600 mb-4">A few basics so I can find your venues:</p>
      <div className="space-y-4">
        <Field label="Where">
          <select value={props.area} onChange={(e) => props.setArea(e.target.value)}
            className="rounded-full border border-stone-300 px-4 py-1.5 text-sm">
            <option>New York City</option>
            <option>New York City & surrounding area</option>
          </select>
        </Field>
        <Field label="Guests">
          <Slider
            min={10} max={500} step={10}
            value={props.guests ?? 120}
            display={props.guests ? `${props.guests}${props.guests >= 500 ? "+" : ""} guests` : "~120 guests (default)"}
            onChange={props.setGuests}
          />
        </Field>
        <Field label="Budget">
          <Slider
            min={5000} max={500000} step={5000}
            value={props.budget ?? 60000}
            display={props.budget ? `$${props.budget.toLocaleString()}${props.budget >= 500000 ? "+" : ""}` : "$60,000 (default)"}
            onChange={props.setBudget}
          />
        </Field>
        <Field label="Season">
          <Chips options={SEASONS.map((s) => ({ v: s, l: s }))} value={props.season} onPick={props.setSeason} />
        </Field>
        <Field label="Style">
          <div className="flex flex-wrap gap-2">
            {STYLE_OPTS.map((s) => {
              const on = props.styles.includes(s);
              const atMax = !on && props.styles.length >= MAX_STYLES;
              return (
                <button key={s} onClick={() => toggleStyle(s)} disabled={atMax}
                  className={`px-3 py-1.5 rounded-full border text-sm capitalize ${on ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"} ${atMax ? "opacity-40" : ""}`}>
                  {s}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <button onClick={props.onSubmit} disabled={props.busy}
        className="mt-5 px-5 py-2 rounded-full bg-stone-900 text-stone-50 text-sm hover:bg-stone-700 disabled:opacity-40">
        {props.busy ? "Saving…" : done ? "Save & continue →" : "Find my venues →"}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="w-20 shrink-0 text-sm text-stone-500">{label}</div>
      <div>{children}</div>
    </div>
  );
}

// Guests + budget are continuous ranges (guests → 500, budget → $500k). The
// displayed value tracks the slider; until the couple touches it the state stays
// null (the thumb sits at a sensible default, the label says "(default)") so the
// brief's smart-default fallback still applies if they never set it.
function Slider({ min, max, step, value, display, onChange }: {
  min: number; max: number; step: number; value: number; display: string; onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 w-full sm:w-80">
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 accent-stone-900 cursor-pointer"
        aria-label={display}
      />
      <span className="w-36 shrink-0 text-sm text-stone-700 tabular-nums">{display}</span>
    </div>
  );
}

function Chips<T extends string | number>({ options, value, onPick }: { options: { v: T; l: string }[]; value: T | null; onPick: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={String(o.v)} onClick={() => onPick(o.v)}
          className={`px-3 py-1.5 rounded-full border text-sm capitalize ${value === o.v ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ─── Reference grid ──────────────────────────────────────────────────────────
// Offering-grouped wedding images: each tile is captioned by WHAT IT SHOWS (a
// venue space, the flower types, a scene) — never the vendor name.
function ReferenceGrid({ cat, loading, tiles, picked, onToggle, getToken, emptyHint }: {
  cat: string; loading: boolean; tiles: Tile[];
  picked: Set<string>; onToggle: (cat: string, tile: Tile) => void;
  getToken: () => Promise<string | null>;
  emptyHint?: string | undefined;
}) {
  if (loading) {
    return <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] rounded-xl bg-stone-100 animate-pulse" />)}</div>;
  }
  if (tiles.length === 0) {
    return <div className="rounded-xl border border-dashed border-stone-300 p-6 text-sm text-stone-500">{emptyHint ?? "No local references for this yet — describe what you're picturing below and I'll choose a great match for you."}</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {tiles.map((t) => {
        const key = `${t.vendor_id}:${t.image_id}`;
        const on = picked.has(key);
        return (
          <button key={key} onClick={() => onToggle(cat, t)}
            className={`group relative rounded-xl overflow-hidden text-left ring-2 transition ${on ? "ring-[var(--bp-accent)]" : "ring-transparent hover:ring-stone-300"}`}>
            <AuthedImage src={`${API_BASE()}/admin/image/${t.image_id}`} alt={t.label} getToken={getToken}
              className="w-full aspect-[4/3] object-cover" placeholderClassName="aspect-[4/3]" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-2 py-1.5">
              {costRangeShort(t.cost_low_cents, t.cost_high_cents) && (
                <span className="block text-[11px] font-semibold text-white">
                  {costRangeShort(t.cost_low_cents, t.cost_high_cents)}
                  <span className="font-normal text-white/70"> est.</span>
                </span>
              )}
              <span className="text-[11px] text-white/90 truncate block">{t.label}</span>
            </div>
            {t.over_budget && (
              <span className="absolute top-2 left-2 rounded-full bg-amber-500/90 text-white text-[10px] px-2 py-0.5">a stretch</span>
            )}
            {on && <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[var(--bp-accent)] text-white text-sm flex items-center justify-center">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─── Music picker — DJ vs live band vendor cards (not an image grid) ──────────
// Feeds on GET /plan/:id/matches (budget-partitioned server-side, so the cards
// agree with the reference grids; over-budget cards carry a "stretch" badge).
interface MusicMatch {
  vendor_id: string; name: string; city: string | null; description: string | null;
  image_ids: string[]; over_budget?: boolean;
  cost_low_cents?: number; cost_high_cents?: number; cost_kind?: "published" | "estimated" | "band";
}
function MusicPicker({ weddingId, getToken, pick, onPick }: {
  weddingId: string | null;
  getToken: () => Promise<string | null>;
  pick: MusicPick | null;
  onPick: (p: MusicPick | null) => void;
}) {
  const [pools, setPools] = useState<{ dj: MusicMatch[]; band: MusicMatch[] } | null>(null);
  useEffect(() => {
    if (!weddingId) return;
    let cancelled = false;
    void (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const [dj, band] = await Promise.all([
        apiGet<{ matches: MusicMatch[] }>(`/plan/${weddingId}/matches?category=dj&limit=3&per_images=1`, token),
        apiGet<{ matches: MusicMatch[] }>(`/plan/${weddingId}/matches?category=band&limit=3&per_images=1`, token),
      ]);
      if (cancelled) return;
      setPools({ dj: dj.ok ? dj.value.matches : [], band: band.ok ? band.value.matches : [] });
    })();
    return () => { cancelled = true; };
  }, [weddingId, getToken]);

  if (!pools) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-xl bg-stone-100 animate-pulse" />)}</div>;
  }
  const lane = (cat: "dj" | "band", title: string, matches: MusicMatch[]) => (
    <div>
      <div className="text-xs uppercase tracking-wide text-stone-400 font-medium mb-2">{title}</div>
      {matches.length === 0 && <p className="text-sm text-stone-500">No local {title.toLowerCase()} references yet.</p>}
      <div className="space-y-2">
        {matches.map((m) => {
          const on = pick?.cat === cat && pick.vendorId === m.vendor_id;
          return (
            <button key={m.vendor_id} aria-pressed={on}
              onClick={() => onPick(on ? null : { cat, vendorId: m.vendor_id, name: m.name, costLowCents: m.cost_low_cents, costHighCents: m.cost_high_cents })}
              className={`w-full text-left rounded-xl border p-3 transition ${on ? "border-[var(--bp-accent)] ring-2 ring-[var(--bp-accent)] bg-[var(--bp-accent)]/5" : "border-stone-300 hover:border-stone-400 hover:bg-stone-50"}`}>
              <div className="flex items-center gap-2">
                <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${on ? "bg-[var(--bp-accent)] text-white" : "bg-stone-200 text-stone-500"}`}>{on ? "✓" : "◦"}</span>
                <span className="font-medium truncate">{m.name}</span>
                {costRangeShort(m.cost_low_cents, m.cost_high_cents) && (
                  <span className="ml-auto shrink-0 text-xs font-medium text-stone-600">{costRangeShort(m.cost_low_cents, m.cost_high_cents)}</span>
                )}
                {m.over_budget && <span className="rounded-full bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5">a stretch</span>}
              </div>
              {m.description && <p className="mt-1 text-xs text-stone-500 line-clamp-2">{m.description}</p>}
            </button>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {lane("dj", "DJ", pools.dj)}
      {lane("band", "Live band", pools.band)}
    </div>
  );
}

// ─── Flower-color chip bar (florals step) ────────────────────────────────────
// Inventory-derived bucket chips (only buckets with local rows render): swatch
// dot + label, multi-select → colors= CSV, a "Clear colors" escape, and an
// honest relax note when the server applied fewer colors than requested.
function ColorChipBar({ chips, selected, applied, onToggle, onClear }: {
  chips: ColorChip[]; selected: string[]; applied: string[] | undefined;
  onToggle: (bucket: string) => void; onClear: () => void;
}) {
  // Relaxed = the server auto-dropped chips to keep the grid from going blank.
  const relaxed = selected.length > 0 && applied != null
    && (applied.length !== selected.length || selected.some((c) => !applied.includes(c)));
  const labelOf = (b: string): string => chips.find((c) => c.bucket === b)?.label ?? b;
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-stone-400 font-medium mr-1">Flower colors</span>
        {chips.map((c) => {
          const on = selected.includes(c.bucket);
          return (
            <button key={c.bucket} onClick={() => onToggle(c.bucket)} aria-pressed={on}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition ${on ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"}`}>
              <span aria-hidden="true" className="inline-block h-3 w-3 shrink-0 rounded-full border border-black/10"
                style={{ backgroundColor: c.swatch_hex }} />
              {c.label}
            </button>
          );
        })}
        {selected.length > 0 && (
          <button onClick={onClear}
            className="px-3 py-1.5 rounded-full border border-stone-300 text-sm text-stone-500 hover:bg-stone-100">
            Clear colors
          </button>
        )}
      </div>
      {relaxed && (
        <p className="mt-2 text-xs text-amber-700">
          Not many {selected.map((b) => labelOf(b).toLowerCase()).join(" and ")} florals nearby — showing your closest matches.
        </p>
      )}
    </div>
  );
}

// ─── Sub-filter bar (venue space / dining style) ─────────────────────────────
function SubFilterBar({ label, options, value, onPick, disabledHint, venueChosen }: {
  label: string; options: { v: string; l: string }[]; value: string | undefined;
  onPick: (v: string) => void; disabledHint: string | undefined; venueChosen: boolean;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-stone-400 font-medium mr-1">{label}</span>
      {options.map((o) => {
        const on = value === o.v;
        // Reception images need a locked venue to scope to — gate that chip.
        const gated = o.v === "reception" && !venueChosen;
        return (
          <button key={o.v} onClick={() => !gated && onPick(o.v)} disabled={gated}
            aria-pressed={on} title={gated ? disabledHint : undefined}
            className={`px-3 py-1.5 rounded-full border text-sm transition ${on ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"} ${gated ? "opacity-40 cursor-not-allowed" : ""}`}>
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

// ─── The Food step's menu bar ─────────────────────────────────────────────────
// Service style persists on the brief (gates the Feast scene + shapes the
// Tablescape/Reception framings); the course tabs / buffet chips only filter
// which reference pictures show.
function FoodMenuBar({ service, onService, dining, onDining, courses, onCourses, course, onCourse, dishInputs, onDishChange, onDishCommit }: {
  service: "buffet" | "sit_down" | undefined; onService: (v: "buffet" | "sit_down") => void;
  dining: "buffet" | "food_station" | undefined; onDining: (v: "buffet" | "food_station") => void;
  courses: 3 | 4 | 5 | undefined; onCourses: (n: 3 | 4 | 5) => void;
  course: string | undefined; onCourse: (v: string) => void;
  dishInputs: Record<string, string>; onDishChange: (slot: string, value: string) => void; onDishCommit: () => void;
}) {
  // The dish slots the couple fills in: one per course (sit-down) or up to 4
  // free slots (buffet). Each typed dish becomes a generated MENU image (§D).
  const dishSlots: { slot: string; label: string; placeholder: string }[] =
    service === "sit_down" && courses
      ? COURSE_SETS[courses].map((c) => ({ slot: c, label: COURSE_LABELS[c] ?? c, placeholder: "e.g. Pan-seared branzino, lemon beurre blanc" }))
      : service === "buffet"
        ? [0, 1, 2, 3].map((i) => ({ slot: `b${i}`, label: `Dish ${i + 1}`, placeholder: "e.g. Herb-roasted chicken" }))
        : [];
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-stone-400 font-medium mr-1">Service</span>
        {([["sit_down", "Sit-down dinner"], ["buffet", "Buffet"]] as const).map(([v, l]) => (
          <button key={v} onClick={() => onService(v)} aria-pressed={service === v}
            className={`px-3 py-1.5 rounded-full border text-sm transition ${service === v ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"}`}>
            {l}
          </button>
        ))}
      </div>
      {service === "buffet" && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-stone-400 font-medium mr-1">Style</span>
          {([["buffet", "Classic buffet"], ["food_station", "Food stations"]] as const).map(([v, l]) => (
            <button key={v} onClick={() => onDining(v)} aria-pressed={dining === v}
              className={`px-3 py-1.5 rounded-full border text-sm transition ${dining === v ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"}`}>
              {l}
            </button>
          ))}
        </div>
      )}
      {service === "sit_down" && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-stone-400 font-medium mr-1">Courses</span>
          {([3, 4, 5] as const).map((n) => (
            <button key={n} onClick={() => onCourses(n)} aria-pressed={courses === n}
              className={`px-3 py-1.5 rounded-full border text-sm transition ${courses === n ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"}`}>
              {n} courses
            </button>
          ))}
        </div>
      )}
      {service === "sit_down" && courses && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-stone-400 font-medium mr-1">Browse by course</span>
          {COURSE_SETS[courses].map((c) => (
            <button key={c} onClick={() => onCourse(c)} aria-pressed={course === c}
              className={`px-3 py-1.5 rounded-full border text-sm transition ${course === c ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:bg-stone-100"}`}>
              {COURSE_LABELS[c] ?? c}
            </button>
          ))}
        </div>
      )}

      {/* §D — type the dishes you'll serve (or leave blank and pick a picture
          below). Each typed dish becomes its own image in your dream-wedding
          menu, plated for a sit-down or styled for a buffet. */}
      {dishSlots.length > 0 && (
        <div className="mb-4 rounded-xl border border-stone-200 bg-stone-50/60 p-3">
          <div className="text-xs uppercase tracking-wide text-stone-400 font-medium mb-2">
            Your menu — type a dish for each {service === "buffet" ? "station" : "course"}
          </div>
          <div className="space-y-2">
            {dishSlots.map(({ slot, label, placeholder }) => (
              <div key={slot} className="flex items-center gap-2">
                <label htmlFor={`dish-${slot}`} className="w-28 shrink-0 text-sm text-stone-500 capitalize">{label}</label>
                <input
                  id={`dish-${slot}`}
                  type="text"
                  value={dishInputs[slot] ?? ""}
                  maxLength={120}
                  placeholder={placeholder}
                  onChange={(e) => onDishChange(slot, e.target.value)}
                  onBlur={onDishCommit}
                  className="flex-1 rounded-lg border border-stone-300 px-3 py-1.5 text-sm focus:border-[var(--bp-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--bp-accent)]"
                />
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-stone-500">We'll render each dish in your wedding's style — leave one blank to skip it.</p>
        </div>
      )}
    </div>
  );
}

// ─── Photography-style picker — sets the global photographic treatment ────────
function PhotoStylePicker({ selected, onPick }: { selected: string | undefined; onPick: (v: string) => void }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {PHOTO_STYLES.map((p) => {
        const on = selected === p.v;
        return (
          <button key={p.v} onClick={() => onPick(p.v)} aria-pressed={on}
            className={`text-left rounded-xl border p-4 transition ${on ? "border-[var(--bp-accent)] ring-2 ring-[var(--bp-accent)] bg-[var(--bp-accent)]/5" : "border-stone-300 hover:border-stone-400 hover:bg-stone-50"}`}>
            <div className="flex items-center gap-2">
              <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${on ? "bg-[var(--bp-accent)] text-white" : "bg-stone-200 text-stone-500"}`}>{on ? "✓" : "◦"}</span>
              <span className="font-medium">{p.l}</span>
            </div>
            <p className="mt-1.5 text-xs text-stone-500">{p.d}</p>
          </button>
        );
      })}
    </div>
  );
}

// ─── Review card (Phase B) ───────────────────────────────────────────────────
function ReviewCard(props: {
  brief: Brief; styles: string[];
  venueName: string | null; floralsName: string | null;
  guests: number | null; budget: number | null; season: string | null;
  canGenerate: boolean; busy: boolean;
  onBack: () => void; onGenerate: () => void;
}) {
  const styleVal = (props.brief.styles?.length ? props.brief.styles : props.styles).join(", ");
  const rows: { label: string; ok: boolean; gating: boolean; value: string; hint: string }[] = [
    { label: "Venue", ok: Boolean(props.venueName), gating: true, value: props.venueName ?? "not chosen", hint: "Pick a venue — it anchors the whole day." },
    { label: "Style & palette", ok: Boolean(styleVal), gating: true, value: styleVal || "not set", hint: "Pick a style so the day feels cohesive." },
    { label: "Flowers", ok: Boolean(props.floralsName), gating: false, value: props.floralsName ?? "I'll choose for you", hint: "" },
    { label: "Guests", ok: Boolean(props.brief.guests ?? props.guests), gating: false, value: (props.brief.guests ?? props.guests)?.toString() ?? "I'll assume ~120", hint: "" },
    { label: "Budget", ok: Boolean(props.brief.budget_cents ?? props.budget), gating: false, value: dollars(props.brief.budget_cents ?? (props.budget ? props.budget * 100 : undefined)), hint: "" },
    { label: "Season", ok: Boolean(props.brief.season ?? props.season), gating: false, value: props.brief.season ?? props.season ?? "I'll assume in-season", hint: "" },
  ];
  return (
    <div className="max-w-xl mx-auto rounded-2xl border border-stone-200 bg-white p-6" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      <h3 className="font-display text-2xl">Ready to dream it up?</h3>
      <p className="text-stone-600 mt-1 mb-5">Here's everything I'll use to compose your day. The first two are needed; the rest I'll fill in thoughtfully if you skip them.</p>
      {/* aria-live so the readiness state is announced as items resolve */}
      <ul className="space-y-3" aria-live="polite">
        {rows.map((r) => (
          <li key={r.label} className="flex items-start gap-3">
            <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${r.ok ? "bg-emerald-600 text-white" : r.gating ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-500"}`}>
              {r.ok ? "✓" : r.gating ? "!" : "•"}
            </span>
            <div className="min-w-0">
              <div className="text-sm"><span className="font-medium">{r.label}</span> — <span className="text-stone-600">{r.value}</span></div>
              {!r.ok && r.gating && <div className="text-xs text-amber-700 mt-0.5">{r.hint}</div>}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex items-center justify-between">
        <button onClick={props.onBack} className="px-4 py-2 rounded-full border border-stone-300 text-sm hover:bg-stone-100">← Keep building</button>
        <button onClick={props.onGenerate} disabled={!props.canGenerate || props.busy}
          className="px-6 py-2.5 rounded-full bg-[var(--bp-accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-40">
          {props.busy ? "Starting…" : "Generate my dream wedding ✦"}
        </button>
      </div>
      {!props.canGenerate && <p className="mt-2 text-xs text-amber-700 text-right">Add a venue and a style to continue.</p>}
    </div>
  );
}

// ─── Composing overlay (Phase C) ─────────────────────────────────────────────
// Reveal early ONLY when the job has genuinely STALLED (no new scene finished for
// this long) — not at a fixed wall-clock, which under slow/throttled renders fired
// mid-generation and yanked the couple off the cinematic overlay "after the 3rd
// scene". As long as scenes keep completing, the overlay stays up until terminal.
const STALL_CEILING_MS = 300_000;   // no scene finished for 5 min ⇒ assume stuck
const ABS_CEILING_MS = 1_200_000;   // 20 min absolute backstop — never strand the user

interface CollageImg { image_id: string; quality_score: number | null; width_px: number | null; height_px: number | null }

/**
 * Shared authed blob pool: fetch each pool image ONCE (cross-origin + a fresh
 * Clerk token — browsers can't header an <img src>), cache its object URL, and
 * revoke every URL on unmount. This lets the collage cards swap freely between
 * pictures with NO refetch or token-mint churn on each swap (the swap just
 * points at an already-cached blob). Concurrency-capped; re-mints on a 401.
 */
function useAuthedBlobPool(ids: string[], getToken: () => Promise<string | null>): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const cacheRef = useRef<Map<string, string>>(new Map());
  const key = ids.join(",");
  useEffect(() => {
    if (ids.length === 0) return;
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      let token = await getToken();
      const todo = ids.filter((id) => !cacheRef.current.has(id));
      let idx = 0;
      const worker = async () => {
        while (!cancelled && idx < todo.length) {
          const id = todo[idx++]!;
          for (let attempt = 0; attempt < 2 && !cancelled; attempt++) {
            if (!token) { token = await getToken(); if (!token) break; }
            try {
              const res = await fetch(`${API_BASE()}/admin/image/${id}`, { headers: { authorization: `Bearer ${token}` }, signal: controller.signal });
              if (cancelled) return;
              if (res.status === 401) { token = await getToken(); continue; } // re-mint + retry once
              if (!res.ok) break;
              const blob = await res.blob();
              if (cancelled) return;
              cacheRef.current.set(id, URL.createObjectURL(blob));
              setUrls(new Map(cacheRef.current));
            } catch { /* aborted/network — skip */ }
            break;
          }
        }
      };
      await Promise.all([worker(), worker(), worker(), worker()]); // concurrency 4
    })();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, getToken]);
  // Revoke every cached object URL on final unmount (no leak across a long wait).
  useEffect(() => () => { for (const u of cacheRef.current.values()) URL.revokeObjectURL(u); cacheRef.current.clear(); }, []);
  return urls;
}

interface CardConfig { pos: { x: string; y: string }; width: string; ar: string; variant: string; delayS: string }

/**
 * One drifting collage card. It OWNS its current picture and swaps to a NEW
 * random one from the pool at each animation-iteration boundary (the keyframe is
 * at opacity 0 there, so the swap is invisible). The 3s/8s/3s fade lives in the
 * CSS keyframes; the card's randomized --delay desyncs it from every other card,
 * so the pictures don't fade in/out together.
 */
function CollageCard({ cfg, pool, blobs }: { cfg: CardConfig; pool: string[]; blobs: Map<string, string> }) {
  const [imgId, setImgId] = useState<string | null>(null);
  // Prefer an already-loaded picture (avoids a blank cycle); fall back to any.
  const pickRef = useRef<() => string | null>(() => null);
  pickRef.current = () => {
    if (pool.length === 0) return null;
    const loaded = pool.filter((id) => blobs.has(id));
    const src = loaded.length ? loaded : pool;
    return src[Math.floor(Math.random() * src.length)]!;
  };
  useEffect(() => { setImgId((cur) => cur ?? pickRef.current()); }, [pool.length, blobs]);
  const url = imgId ? blobs.get(imgId) ?? null : null;
  return (
    <div className="bp-collage-card"
      style={{
        ["--x" as string]: cfg.pos.x, ["--y" as string]: cfg.pos.y, ["--w" as string]: cfg.width,
        ["--delay" as string]: cfg.delayS, ["--float" as string]: cfg.variant, width: cfg.width,
      }}
      onAnimationIteration={() => setImgId(pickRef.current())}>
      <div style={{ aspectRatio: cfg.ar }} className="w-full overflow-hidden bg-stone-200/40">
        {url && <img src={url} alt="" decoding="async" className="w-full h-full object-cover" />}
      </div>
    </div>
  );
}

function ComposingOverlay({ jobId, weddingId, getToken, onReveal, onError }: {
  jobId: string; weddingId: string | null;
  getToken: () => Promise<string | null>;
  onReveal: () => void; onError: (msg: string) => void;
}) {
  const [moments, setMoments] = useState<MomentOut[]>([]);
  const [planned, setPlanned] = useState<PlannedLabel[]>([]);
  const [status, setStatus] = useState<JobResponse["job"]["status"]>("queued");
  const [paused, setPaused] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [collage, setCollage] = useState<CollageImg[]>([]);
  const startRef = useRef<number>(Date.now());
  const lastProgressRef = useRef<number>(Date.now());
  const prevDoneRef = useRef<number>(0);
  const revealedRef = useRef(false);
  const pauseBtnRef = useRef<HTMLButtonElement>(null);
  // One stable seed per mount so card size/variant/delay don't reshuffle on re-render.
  const seedRef = useRef<number>(0);
  if (seedRef.current === 0) seedRef.current = Math.floor(Math.random() * 1e9) || 1;

  // Move focus into the overlay on mount (the pause control must be reachable).
  useEffect(() => { pauseBtnRef.current?.focus(); }, []);

  // Fetch the RANDOM 50-image brief-criteria RAG pool. The cards rotate through
  // these on their own timers (NOT the couple's specific picks — a fresh random
  // set, on-criteria, each load).
  useEffect(() => {
    if (!weddingId) return;
    let cancelled = false;
    void (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<{ images: CollageImg[] }>(`/plan/${weddingId}/collage?limit=50`, token);
      if (!cancelled && res.ok) setCollage(res.value.images);
    })();
    return () => { cancelled = true; };
  }, [weddingId, getToken]);

  const pool = useMemo(() => collage.map((c) => c.image_id), [collage]);
  const blobs = useAuthedBlobPool(pool, getToken);

  // Six stable card slots: position/size/aspect/drift/delay fixed per mount; the
  // PICTURE each shows is random and swaps over time. Each slot's randomized
  // (negative) --delay starts it mid-cycle, so no two cards fade in/out together.
  const cardConfigs = useMemo<CardConfig[]>(() => {
    const rng = seededRng(seedRef.current);
    return POSITIONS.map((pos) => ({
      pos,
      width: SIZE_WIDTHS[Math.floor(rng() * SIZE_WIDTHS.length)]!,
      ar: ASPECTS[Math.floor(rng() * ASPECTS.length)]!,
      variant: FLOAT_VARIANTS[Math.floor(rng() * FLOAT_VARIANTS.length)]!,
      delayS: `-${(rng() * 14).toFixed(1)}s`,
    }));
  }, []);

  const fireReveal = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setRevealing(true);
    setTimeout(onReveal, 700); // let the curtain-lift play, then show the carousel
  }, [onReveal]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      const token = await getToken();
      if (!token) { timer = setTimeout(poll, 3_000); return; }
      const res = await apiGet<JobResponse>(`/jobs/${jobId}`, token);
      if (res.ok) {
        setMoments(res.value.moments);
        if (res.value.job.planned_labels?.length) setPlanned(res.value.job.planned_labels);
        setStatus(res.value.job.status);
        const terminal = res.value.job.status !== "queued" && res.value.job.status !== "running";
        if (res.value.job.status === "failed" && !res.value.moments.some((m) => m.image_url)) {
          onError(res.value.job.error ?? "Generation failed — please try again.");
          return;
        }
        // Reveal ONLY when terminal (incl. 'partial'), so one stuck moment can't
        // hold the curtain forever — but never before every image is composed.
        if (terminal) { fireReveal(); return; }
        // Track progress so the stall-ceiling resets each time a scene finishes —
        // keeps the cinematic overlay up while generation is still advancing.
        const doneNow = res.value.moments.filter((m) => m.image_url).length;
        if (doneNow > prevDoneRef.current) { prevDoneRef.current = doneNow; lastProgressRef.current = Date.now(); }
        // Reveal early only if STALLED (no new scene for a while) or past the
        // absolute backstop — and only with something to show.
        const now = Date.now();
        const stalled = now - lastProgressRef.current > STALL_CEILING_MS;
        const pastAbsolute = now - startRef.current > ABS_CEILING_MS;
        if ((stalled || pastAbsolute) && res.value.moments.some((m) => m.image_url)) {
          fireReveal(); return;
        }
      }
      timer = setTimeout(poll, 2_500);
    };
    void poll();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, getToken, fireReveal]);

  // Checklist over the FULL planned set in PROCESSING ORDER (builder plan §E):
  // the server (0032) returns every scene it will render, ordered, from the first
  // poll — so the list shows all rows immediately and never grows 6→10. Each row
  // flips ✓ when its moment has an image. Legacy jobs (no planned_labels) fall
  // back to the old provisional ∪ extras union.
  const doneByLabel = new Map(moments.map((m) => [m.label, Boolean(m.image_url)]));
  const orderedLabels = planned.length > 0
    ? planned.map((p) => p.label)
    : [...PROVISIONAL, ...new Set(moments.map((m) => m.label).filter((l) => !PROVISIONAL.includes(l)))];
  const checklist = orderedLabels.map((label) => ({ label, done: doneByLabel.get(label) ?? false }));
  const doneCount = checklist.filter((c) => c.done).length;
  const total = checklist.length;
  const current = checklist.find((c) => !c.done)?.label ?? checklist[checklist.length - 1]?.label ?? "your day";
  // Slow-start / fast-finish perceived progress.
  const frac = total > 0 ? doneCount / total : 0;
  const barPct = Math.round((revealing ? 1 : Math.min(0.97, Math.pow(frac, 0.7))) * 100);

  return (
    <div className={`fixed inset-0 z-50 overflow-hidden bp-aura ${paused ? "bp-paused" : ""} ${revealing ? "opacity-0 transition-opacity duration-700" : ""}`}>
      {/* Rolling collage: a random sample of brief-matched inspiration, each card
          drifting on its own timer and swapping pictures from the 50-image pool. */}
      <div aria-hidden="true" className="absolute inset-0">
        {cardConfigs.map((cfg, i) => (
          <CollageCard key={i} cfg={cfg} pool={pool} blobs={blobs} />
        ))}
      </div>

      {/* Centerpiece: progress copy + checklist (the source of truth) */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        <div className="w-full max-w-md rounded-2xl bg-white/70 backdrop-blur-sm border border-white/60 p-6" style={{ boxShadow: "var(--bp-shadow-card)" }}>
          <div className="text-xs uppercase tracking-wide text-[var(--bp-accent)] font-medium">Gathering your inspiration</div>
          <h3 className="font-display text-2xl mt-1" aria-live="polite">
            {revealing ? "Your day is ready ✦" : `${verbFor(current)}…`}
          </h3>
          <p className="text-sm text-stone-600 mt-1" aria-live="polite">{doneCount} of {total} moments composed</p>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-stone-200 overflow-hidden">
            <div className="h-full bg-[var(--bp-accent)] transition-all duration-700" style={{ width: `${barPct}%` }} />
          </div>

          {/* Checklist — a status list (not interactive checkboxes) */}
          <ul className="mt-4 space-y-1.5 text-left">
            {checklist.map((c) => (
              <li key={c.label} className="flex items-center gap-2 text-sm">
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${c.done ? "bg-emerald-600 text-white" : "bg-stone-200 text-stone-400"}`}>
                  {c.done ? "✓" : "•"}
                </span>
                <span className={c.done ? "text-stone-800" : "text-stone-500"}>{c.label}</span>
                <span className="sr-only">{c.done ? "— done" : "— in progress"}</span>
              </li>
            ))}
          </ul>

          <p className="mt-4 text-xs text-stone-500">I'll reveal your whole wedding at once, the moment every picture is ready.</p>
          <button ref={pauseBtnRef} onClick={() => setPaused((p) => !p)}
            className="mt-3 text-xs text-stone-500 underline underline-offset-4 hover:text-stone-800">
            {paused ? "Resume motion" : "Reduce motion"}
          </button>
        </div>
        {status === "partial" && (
          <p className="relative z-10 mt-3 text-xs text-amber-700">A couple of moments are still finishing — you can retry them on the next screen.</p>
        )}
      </div>
    </div>
  );
}
